import { useState } from "react";
import { Link, useLocation } from "wouter";
import { login, api, setSession } from "../lib/api";
import { Shell } from "../components/Shell";

type Mode = "signin" | "signup";

export default function Login() {
  const [, navigate] = useLocation();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const next = new URLSearchParams(window.location.search).get("next") ?? "/me/bookings";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (mode === "signin") {
        await login(email.trim(), password);
      } else {
        const s = await api.signup({
          email: email.trim(),
          password,
          display_name: displayName.trim() || undefined,
          phone: phone.trim() || undefined,
        });
        setSession(s);
      }
      navigate(next);
    } catch (e: any) {
      setErr(e.message ?? "Could not sign in");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <div className="mx-auto max-w-md px-6 py-20">
        <h1 className="text-3xl font-semibold tracking-tight">
          {mode === "signin" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="mt-2 text-neutral-400 text-sm">
          {mode === "signin"
            ? "Sign in to book and manage your tee times."
            : "It only takes a moment."}
        </p>

        <div className="mt-6 flex items-center gap-1 text-xs">
          {(["signin", "signup"] as Mode[]).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={"rounded-full px-3 py-1.5 transition-colors " +
                (mode === m ? "bg-neutral-100 text-neutral-950" : "text-neutral-400 hover:text-neutral-0")}
            >
              {m === "signin" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            type="email" required placeholder="you@example.com"
            value={email} onChange={e => setEmail(e.target.value)}
            className="w-full rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
          />
          <input
            type="password" required placeholder="Password"
            minLength={mode === "signup" ? 8 : undefined}
            value={password} onChange={e => setPassword(e.target.value)}
            className="w-full rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
          />
          {mode === "signup" && (
            <>
              <input
                type="text" placeholder="Your name"
                value={displayName} onChange={e => setDisplayName(e.target.value)}
                className="w-full rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
              />
              <input
                type="tel" placeholder="Phone (optional)"
                value={phone} onChange={e => setPhone(e.target.value)}
                className="w-full rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
              />
            </>
          )}

          {err && <div className="rounded-xl border border-red-900/50 bg-red-950/30 text-red-200 p-4 text-sm">{err}</div>}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center rounded-full bg-accent-500 text-neutral-950 px-6 py-3 text-sm font-medium hover:bg-accent-400 transition-colors disabled:opacity-50"
          >
            {busy ? "Please wait…" : (mode === "signin" ? "Sign in" : "Create account")}
          </button>
        </form>

        <p className="mt-6 text-xs text-neutral-500 text-center">
          By continuing you agree to our terms. <Link href="/" className="text-neutral-300 hover:text-neutral-0">Back home</Link>
        </p>
      </div>
    </Shell>
  );
}
